import {action, makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {ConnextraType, ModalType, RoundStatus} from "data/enums";
import type {ICrunchStore} from "data/stores/crunch/crunch.store";
import {createConnextraScriptTag} from "data/utils";
import type {IUserStore} from "data/stores/user/user.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IAuthController} from "views/controllers/auth/auth.controller";

export interface IPickStickyBarController extends ViewController {
	get allTournaments(): number;
	get allPicks(): number;
	get isChanged(): boolean;
	get isSaved(): boolean;
	get isRoundComplete(): boolean;
	get isAuthorized(): boolean;

	onSavePicks: () => void;
	onAutofill: () => void;
	onClear: () => void;
	onClinkLogin: () => void;
	onClinkReg: () => void;
}

@injectable()
export class PickStickyBarController implements IPickStickyBarController {
	constructor(
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.CrunchStore) private _crunchStore: ICrunchStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.AuthController) private _authController: IAuthController
	) {
		makeAutoObservable(this);
	}

	get allTournaments(): number {
		return this._roundsStore.tournamentsBySelectedRound.length;
	}

	get allPicks(): number {
		return this._predictionsStore.list.length;
	}

	get isChanged(): boolean {
		return this._predictionsStore.isChanged || this._crunchStore.isChanged;
	}

	get isSaved(): boolean {
		return this._predictionsStore.isSaved;
	}

	get isRoundComplete(): boolean {
		return this._roundsStore.currentSelectedRound?.status === RoundStatus.Complete;
	}

	get isAuthorized(): boolean {
		return this._userStore.isAuthorized;
	}

	dispose(): void {
		return;
	}

	@action
	public onSavePicks = (): void => {
		if (!this._userStore.isAuthorized) {
			this._modalsStore.showModal(ModalType.LEAVE_PICKS);
		} else {
			if (this.allPicks) {
				void this._predictionsStore.savePredictionsAnswers();
			}

			if (this._crunchStore.isChanged) {
				void this._crunchStore.saveCrunch();
			}

			const AccountID = this._userStore.user?.id ?? "";
			const params = `AccountID=${AccountID}`;
			createConnextraScriptTag(ConnextraType.PICK_CONFIRM, params);
		}
	};

	onClinkLogin = () => {
		this._predictionsStore.setPredictionsTemporaryAnswers();

		this._authController.trackLoginEvent();
	};

	@action
	public onClinkReg = (): void => {
		this._predictionsStore.setPredictionsTemporaryAnswers();

		this._authController.trackRegEvent();
	};

	@action
	public onAutofill = (): void => {
		void this._predictionsStore.fetchAutofill();
	};

	@action
	public onClear = (): void => {
		this._predictionsStore.clearSelectedPrediction();
	};

	init(): void {
		return;
	}
}
